// manageazure.js
import React, { useEffect, useMemo, useState } from "react";
import Header from "../header";
import {
  Grid,
  Row,
  Col,
  Panel,
  FormGroup,
  ControlLabel,
  FormControl,
  Button,
  Alert,
  Label,
} from "react-bootstrap";

// Tabulator (Bootstrap 3 theme)
import { ReactTabulator } from "react-tabulator";

import "./manageazure.css";

// axios helpers
import {
  getClientDetails,
  getDsRoles,
  getRolePresets,
  putRolePresets,
  createAzureMapping,
  getAzureMappingsByClient,
} from "../../libs/API";

export default function ManageAzureTwoRole(props) {
  // ----- Select Client -----
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");

  // ----- Roles & Presets -----
  const [dsRoles, setDsRoles] = useState([]);
  const [preset, setPreset] = useState({
    ReadOnly: new Set(),
    SuperUser: new Set(),
  });
  const [loading, setLoading] = useState(true);
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetSaved, setPresetSaved] = useState(false);

  // ----- Mapping form -----
  const [f, setF] = useState({
    dsClientId: "",
    azureIdp: "",
    tenantId: "",
    azureClientId: "",
    azureClientSecret: "",
    aadSuperUser: "",
    aadReadOnly: "",
  });

  // ----- Page state -----
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  // auto-dismiss success/info after 2.5s
  useEffect(() => {
    if (!msg?.text) return;
    if (msg.type === "success" || msg.type === "info") {
      const t = setTimeout(() => setMsg({ type: "", text: "" }), 2500);
      return () => clearTimeout(t);
    }
  }, [msg]);

  useEffect(() => {
    async function init() {
      setLoading(true);
      setMsg({ type: "", text: "" });
      try {
        const [clientList, roles, presets] = await Promise.all([
          getClientDetails(false),
          getDsRoles(),
          getRolePresets(),
        ]);
        setClients(clientList || []);
        setDsRoles(roles || []);
        setPreset({
          ReadOnly: new Set((presets && presets.readOnly) || []),
          SuperUser: new Set((presets && presets.superUser) || []),
        });

        const fromProp = props?.props?.clientId
          ? String(props.props.clientId)
          : "";
        if (fromProp) await handleClientSelect(fromProp);
      } catch (e) {
        console.error(e);
        setMsg({ type: "danger", text: "Load failed." });
      } finally {
        setLoading(false);
      }
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When client changes: populate form from latest mapping if any
  async function handleClientSelect(cid) {
    // clear any previous banners when switching clients
    setMsg({ type: "", text: "" });
    setSaving(false);

    setSelectedClientId(cid);
    setF((prev) => ({ ...prev, dsClientId: cid }));
    if (!cid) return;

    try {
      const list = await getAzureMappingsByClient(cid);
      if (Array.isArray(list) && list.length > 0) {
        const latest = list
          .slice()
          .sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
          )[0];
        setF((prev) => ({
          ...prev,
          dsClientId: cid,
          azureIdp: latest.azureIdp || "",
          tenantId: (latest.tenantId || "").toString(),
          azureClientId: (latest.azureClientId || "").toString(),
          azureClientSecret: (latest.azureClientSecret || "").toString(),
          aadSuperUser: (latest.aadGroupId_SuperUser || "").toString(),
          aadReadOnly: (latest.aadGroupId_ReadOnly || "").toString(),
        }));
      } else {
        setF((prev) => ({
          ...prev,
          dsClientId: cid,
          azureIdp: "",
          tenantId: "",
          azureClientId: "",
          azureClientSecret: "",
          aadSuperUser: "",
          aadReadOnly: "",
        }));
      }
    } catch (e) {
      console.error(e);
      setMsg({
        type: "warning",
        text: "Unable to load existing mapping for this client.",
      });
    }
  }

  function onClientChange(e) {
    const cid = e.target.value;
    handleClientSelect(cid);
  }

  // ----- Preset autosave (tick/untick) -----
  function computeNextSets(roleKey, id, checked) {
    const ro = new Set(preset.ReadOnly);
    const su = new Set(preset.SuperUser);
    const bag = roleKey === "ReadOnly" ? ro : su;
    if (checked) bag.add(id);
    else bag.delete(id);
    return { ro, su };
  }

  async function applyPresetChange(roleKey, id, checked, cell) {
    const next = computeNextSets(roleKey, id, checked);
    setPresetSaving(true);
    setPresetSaved(false);
    try {
      await putRolePresets({
        readOnly: Array.from(next.ro),
        superUser: Array.from(next.su),
      });
      setPreset({ ReadOnly: next.ro, SuperUser: next.su });
      setPresetSaved(true);
      setTimeout(() => setPresetSaved(false), 1000);
    } catch (e) {
      try {
        cell.setValue(!checked, true);
      } catch (_) {}
      setMsg({ type: "danger", text: "Saving presets failed." });
    } finally {
      setPresetSaving(false);
    }
  }

  const onPresetCellEdited = (cell) => {
    const field = cell.getField();
    const row = cell.getRow().getData();
    const checked = cell.getValue() === true;
    if (field === "ro") applyPresetChange("ReadOnly", row.id, checked, cell);
    if (field === "su") applyPresetChange("SuperUser", row.id, checked, cell);
  };

  // Simple "all fields present" validation
  const isFormFilled = useMemo(() => {
    const vals = [
      f.dsClientId,
      f.azureIdp,
      f.tenantId,
      f.azureClientId,
      f.azureClientSecret,
      f.aadSuperUser,
      f.aadReadOnly,
    ];
    return vals.every((v) => String(v || "").trim().length > 0);
  }, [f]);

  // Submit (works via form submit + Enter key)
  async function addOrUpdateMapping(e) {
    if (e && e.preventDefault) e.preventDefault();
    // clear previous banners on a new save run
    setMsg({ type: "", text: "" });

    if (!isFormFilled || saving) return;

    const payload = {
      dsClientId: f.dsClientId.trim(),
      azureIdp: f.azureIdp.trim(),
      tenantId: f.tenantId.trim(),
      azureClientId: f.azureClientId.trim(),
      azureClientSecret: f.azureClientSecret,
      aadGroupId_SuperUser: f.aadSuperUser.trim(),
      aadGroupId_ReadOnly: f.aadReadOnly.trim(),
    };

    try {
      setSaving(true);
      await createAzureMapping(payload);
      setMsg({ type: "success", text: "Mapping saved." });
      // keep the secret value in the field (do not clear) since you want it visible after save
    } catch (err) {
      console.error(err);
      setMsg({
        type: "danger",
        text: err?.message || "Saving mapping failed.",
      });
    } finally {
      setSaving(false);
    }
  }

  // ===== Tabulator: Presets =====
  const presetData = useMemo(
    () =>
      dsRoles.map((r) => ({
        id: r.id,
        name: r.name,
        roleDescription: r.roleDescription,
        ro: preset.ReadOnly.has(r.id),
        su: preset.SuperUser.has(r.id),
      })),
    [dsRoles, preset]
  );

  const presetColumns = [
    { title: "Id", field: "id", width: 60, headerSort: true },
    { title: "Name", field: "name", headerSort: true, formatter: "plaintext" },
    { title: "Description", field: "roleDescription" },
    {
      title: "Read-Only",
      field: "ro",
      width: 110,
      hozAlign: "center",
      formatter: "tickCross",
      editor: true,
    },
    {
      title: "SuperUser",
      field: "su",
      width: 110,
      hozAlign: "center",
      formatter: "tickCross",
      editor: true,
    },
  ];

  const mb10 = { marginBottom: 10 };

  // handy helper to clear a lingering success when the user edits any field
  const clearSuccessOnChange = (updater) => (e) => {
    if (msg.type === "success") setMsg({ type: "", text: "" });
    updater(e);
  };

  return (
    <div className="manageusers-main">
      <Header props={props.props} history={props.history} />

      <Grid fluid>
        {/* Title + Client Select */}
        <Row style={{ marginTop: 15, marginBottom: 12 }}>
          <Col xs={12} sm={7}>
            <h2 style={{ marginTop: 0, fontWeight: 600 }}>
              Azure Mapping{" "}
              <small className="text-muted">(ReadOnly &amp; SuperUser)</small>
            </h2>
          </Col>
          <Col xs={12} sm={5}>
            <FormGroup style={{ marginBottom: 0 }}>
              <ControlLabel>Select Client</ControlLabel>
              <FormControl
                componentClass="select"
                value={selectedClientId || ""}
                onChange={onClientChange}
              >
                <option value="">Select Client</option>
                {clients.map((c) => (
                  <option key={String(c.clientId)} value={String(c.clientId)}>
                    {c.clientName}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </Col>
        </Row>

        {/* Alerts */}
        {msg.text ? (
          <Row>
            <Col xs={12}>
              <Alert
                bsStyle={msg.type || "info"}
                style={{ marginTop: 5, marginBottom: 8 }}
              >
                {msg.text}
              </Alert>
            </Col>
          </Row>
        ) : null}

        <Row>
          {/* LEFT: Presets (scrollable, autosave) */}
          <Col xs={12} md={7} className="manageazure-left">
            <Panel className="panel-default" style={{ height: "100%" }}>
              <Panel.Heading
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <strong>Role → DsRole Presets</strong>
                <div>
                  {presetSaving && <Label bsStyle="info">Saving…</Label>}
                  {!presetSaving && presetSaved && (
                    <Label bsStyle="success">Saved</Label>
                  )}
                </div>
              </Panel.Heading>
              <Panel.Body>
                {loading ? (
                  <div>Loading…</div>
                ) : (
                  <div className="tabulator-fixedheight">
                    <ReactTabulator
                      data={presetData}
                      columns={presetColumns}
                      options={{
                        layout: "fitColumns",
                        height: "100%",
                        tooltips: true,
                        placeholder: "No roles.",
                      }}
                      onRef={(ref) => {
                        if (ref && ref.table) ref.table.setHeight(520);
                      }}
                      cellEdited={onPresetCellEdited}
                      className="tabulator-bootstrap3"
                    />
                  </div>
                )}
              </Panel.Body>
            </Panel>
          </Col>

          {/* RIGHT: Mapping form */}
          <Col xs={12} md={5} className="manageazure-right">
            <Panel className="panel-default" style={{ height: "100%" }}>
              <Panel.Heading>
                <strong>Add / Update Azure Client Mapping</strong>
              </Panel.Heading>
              <Panel.Body>
                <form onSubmit={addOrUpdateMapping}>
                  <Row>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>DS Client Id</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="text"
                          value={f.dsClientId}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, dsClientId: e.target.value })
                          )}
                          readOnly={!selectedClientId}
                          placeholder="auto from client"
                          style={{ fontFamily: "monospace" }}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>Azure IdP (alias)</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="text"
                          value={f.azureIdp}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, azureIdp: e.target.value })
                          )}
                          placeholder="e.g. AzureAD-Prod"
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>Tenant Id (GUID)</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="text"
                          value={f.tenantId}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, tenantId: e.target.value })
                          )}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          style={{ fontFamily: "monospace" }}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>Azure Client Id (GUID)</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="text"
                          value={f.azureClientId}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, azureClientId: e.target.value })
                          )}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          style={{ fontFamily: "monospace" }}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>Azure Client Secret</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="password"
                          value={f.azureClientSecret}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, azureClientSecret: e.target.value })
                          )}
                          placeholder="paste secret"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>AAD Group Id — SuperUser</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="text"
                          value={f.aadSuperUser}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, aadSuperUser: e.target.value })
                          )}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          style={{ fontFamily: "monospace" }}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} sm={6}>
                      <FormGroup style={mb10}>
                        <ControlLabel>AAD Group Id — Read-Only</ControlLabel>
                        <FormControl
                          bsSize="sm"
                          type="text"
                          value={f.aadReadOnly}
                          onChange={clearSuccessOnChange((e) =>
                            setF({ ...f, aadReadOnly: e.target.value })
                          )}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          style={{ fontFamily: "monospace" }}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ height: 54 }} />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} className="text-right">
                      <Button
                        id="btnSaveMapping"
                        type="submit"
                        bsStyle="success"
                        disabled={!isFormFilled || saving}
                        className={saving ? "btn-saving" : ""}
                        aria-busy={saving}
                      >
                        {saving ? "Saving…" : "Save Mapping"}
                      </Button>
                    </Col>
                  </Row>
                </form>
              </Panel.Body>
            </Panel>
          </Col>
        </Row>
      </Grid>
    </div>
  );
}
